import { frontendURL } from 'dashboard/helper/URLHelper';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import PaginaDosFlows from './PaginaDosFlows.vue';
import PaginaDasExecucoes from './PaginaDasExecucoes.vue';
import PaginaDaExecucao from './PaginaDaExecucao.vue';
import PaginaDoCofre from './PaginaDoCofre.vue';
import PaginaDoGatilho from './PaginaDoGatilho.vue';
import PaginaDasFerramentas from '../settings/powerTools/Index.vue';

// FLOW-06 (SPEC S5, R10; PLAN G18; Task 6.1) — a aba PowerFlow: Flows, Execuções (a lista e o
// detalhe), Cofre e Ferramentas, no casco do OS, como o kanban (`kanban.routes.js`). O editor de flow
// continua em tela cheia, agora em `powerflow/…`. Configurações › PowerTools deixou de existir: os
// endereços dela e os do editor redirecionam para os daqui, no molde do `reports.routes.js`, e os
// nomes de rota antigos continuam resolvendo, para link salvo e favorito não caírem em 404.
//
// **A régua** (Gate 1, Q4) é a do item PowerAgents e a do gestor do cofre (`useGestorDoCofre`): o
// super-admin sempre e, na conta com a flag PowerAgents, o administrador e quem tem
// `poweragents_manage` num papel. O atendente fica de fora. É a mesma do servidor
// (`ReguaDaAbaPowerflow`, `super_admin? || gestor_com_flag?`). A porta da frente é o guard de
// `routes/index.js`: `superAdminOrFlag` deixa o super-admin passar sem a flag e manda os outros ao
// painel quando ela está desligada na conta, e `permissions` tira o atendente. A meta vai em cada
// rota, porque nenhuma é filha de outra.
export const REGUA_DA_ABA = {
  superAdminOrFlag: FEATURE_FLAGS.POWER_AGENTS,
  featureFlag: FEATURE_FLAGS.POWER_AGENTS,
  permissions: ['administrator', 'poweragents_manage'],
  // ACESSO-01 (L8b): com a chave ACESSO_GRANULAR ligada, quem vê os flows, as execuções e os gatilhos
  // (`powerflow_flows#index`, `powerflow_execucoes#index`, `powerflow_gatilhos#index`). O Cofre, as Ferramentas e o
  // editor pedem o deles, abaixo. A flag continua por cima, como no servidor.
  escopos: ['powerflow:read'],
};

// Deploy do PowerFlow (D2, 16/10): sem a chave da instalação `POWERFLOW_ADMIN_MONTA_FLOW`, só o
// super-admin entra no editor; o gestor volta para a lista de Flows, que diz o motivo
// (`?aviso=montar_flow`, em `PaginaDosFlows.vue`). Quem lê a meta é o guard de `routes/index.js`.
const META_DO_EDITOR = {
  ...REGUA_DA_ABA,
  // ACESSO-01 (L8b): montar o flow (`tool_catalog#create`, `#update`, `#compilar`, `#schema`). A chave da instalação,
  // abaixo, continua por cima do escopo, como no servidor (`gestor_monta_flow?`).
  escopos: ['powerflow:write'],
  semChave: {
    chave: 'powerflowAdminMontaFlow',
    volta: 'powerflow/flows',
    query: { aviso: 'montar_flow' },
  },
};

// O editor vem por import dinâmico: o Monaco e os workers dele (cerca de 1 MB gzip, FLOW-01 P11) só
// baixam para quem abre o editor, num chunk fora do bundle do painel.
const FlowEditor = () => import('../settings/powerTools/flow/FlowEditor.vue');

// Configurações › PowerTools tinha duas abas: Ferramentas, a que abria sem `?aba`, e Segredos
// (`?aba=segredos`, a do item do menu). Cada uma virou uma seção.
const daPaginaAntiga = to => ({
  name:
    to.query.aba === 'segredos' ? 'powerflow_cofre' : 'powerflow_ferramentas',
  params: { accountId: to.params.accountId },
  query: {},
});

export const routes = [
  {
    path: frontendURL('accounts/:accountId/powerflow'),
    redirect: to => ({
      name: 'powerflow_flows',
      params: { accountId: to.params.accountId },
    }),
  },
  {
    path: frontendURL('accounts/:accountId/powerflow/flows'),
    name: 'powerflow_flows',
    component: PaginaDosFlows,
    meta: REGUA_DA_ABA,
  },
  // FLOW-03a (PLAN §2.3; Task 7.3): o detalhe de um gatilho, pelo nome do flow e pelo `id` do
  // gatilho no YAML, dentro da seção Flows.
  {
    path: frontendURL(
      'accounts/:accountId/powerflow/flows/:nome/gatilhos/:gatilhoId'
    ),
    name: 'powerflow_gatilho',
    component: PaginaDoGatilho,
    meta: REGUA_DA_ABA,
    props: route => ({
      nome: String(route.params.nome),
      gatilhoId: String(route.params.gatilhoId),
    }),
  },
  {
    path: frontendURL('accounts/:accountId/powerflow/execucoes'),
    name: 'powerflow_execucoes',
    component: PaginaDasExecucoes,
    meta: REGUA_DA_ABA,
  },
  {
    path: frontendURL('accounts/:accountId/powerflow/execucoes/:execucaoId'),
    name: 'powerflow_execucao',
    component: PaginaDaExecucao,
    meta: REGUA_DA_ABA,
    props: route => ({ execucaoId: String(route.params.execucaoId) }),
  },
  {
    path: frontendURL('accounts/:accountId/powerflow/cofre'),
    name: 'powerflow_cofre',
    component: PaginaDoCofre,
    // ACESSO-01 (L8b): a lista dos nomes do cofre (`flow_secrets#index`).
    meta: { ...REGUA_DA_ABA, escopos: ['vault:use'] },
  },
  {
    path: frontendURL('accounts/:accountId/powerflow/ferramentas'),
    name: 'powerflow_ferramentas',
    component: PaginaDasFerramentas,
    // ACESSO-01 (L8b): a tela é o formulário das credenciais das ferramentas (`tool_instances#update_secrets`). A lista
    // é do `poweragents:read`, mas sem gravar a tela não serve.
    meta: { ...REGUA_DA_ABA, escopos: ['poweragents:write'] },
  },
  {
    path: frontendURL('accounts/:accountId/settings/power-tools'),
    name: 'power_tools_index',
    redirect: daPaginaAntiga,
  },
];

export const standaloneRoutes = [
  {
    path: frontendURL('accounts/:accountId/powerflow/flows/novo'),
    name: 'powerflow_flow_novo',
    component: FlowEditor,
    meta: META_DO_EDITOR,
    // FLOW-03b (Task 3.7): `?agente=` é o agente de onde o flow novo veio (o "Novo flow" da tela dele).
    props: route => ({
      accountId: Number(route.params.accountId),
      agente: route.query.agente || null,
    }),
  },
  // FLOW-13 (SPEC S4; PLAN G12): a ferramenta HTTP nova nasce no editor de flow, na vista dela, com
  // o modelo da forma; `?agente=` diz de qual agente ela veio ("Ligar a este agente", R12).
  {
    path: frontendURL('accounts/:accountId/powerflow/ferramentas-http/nova'),
    name: 'powerflow_ferramenta_http_nova',
    component: FlowEditor,
    meta: META_DO_EDITOR,
    props: route => ({
      accountId: Number(route.params.accountId),
      ferramentaHttp: true,
      agente: route.query.agente || null,
    }),
  },
  {
    path: frontendURL('accounts/:accountId/powerflow/flows/:id'),
    name: 'powerflow_flow_editor',
    component: FlowEditor,
    meta: META_DO_EDITOR,
    // `?sessao=` é o "depurar no editor" de uma execução de teste (FLOW-01 D17). FLOW-13: `?agente=`
    // é o agente de onde a ferramenta HTTP veio, e `?vista=canvas`, o "Abrir no editor de flow".
    // FLOW-06 (Task 6.8): `?execucao=` é o "Abrir no editor" de uma execução de produção, que abre o
    // canvas aceso com o que ela guardou, só leitura.
    props: route => ({
      accountId: Number(route.params.accountId),
      id: Number(route.params.id),
      sessao: route.query.sessao || null,
      execucaoDeProducaoId: route.query.execucao || null,
      agente: route.query.agente || null,
      vistaForcada: route.query.vista || null,
    }),
  },
  // Os endereços do editor em Configurações › PowerTools. O redirect por nome leva o `:id` e a query
  // (`?sessao=`, `?agente=`, `?vista=`, o `?executar=` do flow novo) como vieram.
  {
    path: frontendURL('accounts/:accountId/settings/power-tools/flows/novo'),
    name: 'power_tools_flow_new',
    redirect: { name: 'powerflow_flow_novo' },
  },
  {
    path: frontendURL(
      'accounts/:accountId/settings/power-tools/ferramentas-http/nova'
    ),
    name: 'power_tools_http_new',
    redirect: { name: 'powerflow_ferramenta_http_nova' },
  },
  {
    path: frontendURL('accounts/:accountId/settings/power-tools/flows/:id'),
    name: 'power_tools_flow',
    redirect: { name: 'powerflow_flow_editor' },
  },
];

export default { routes, standaloneRoutes };
